import React, { useRef, useEffect } from "react";
import { TextField, Box, IconButton } from "@mui/material";
import AsyncButton from './AsyncButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { addDays, addMonths, format, parseISO, startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";

const DateSelector = ({ date, setDate, disabled = false, mode = 'day' }) => {
  const dateInputRef = useRef();
  const { t } = useTranslation();


  // If no date is provided by the parent, initialize to today's date
  useEffect(() => {
    if ((!date || date === "") && typeof setDate === "function") {
      const today = format(new Date(), "yyyy-MM-dd");
      setDate(today);
    }
  }, [date, setDate]);

  // Use an effective date value to avoid parsing errors when `date` is falsy
  const effectiveDate = date || format(new Date(), "yyyy-MM-dd");

  const normalize = (d) => {
    if (mode === 'month') return format(startOfMonth(d), 'yyyy-MM-dd');
    return format(d, 'yyyy-MM-dd');
  };

  const handlePrev = () => {
    if (disabled) return;
    const currentDate = parseISO(effectiveDate);
    const nextDate = mode === 'month' ? addMonths(currentDate, -1) : addDays(currentDate, -1);
    setDate(normalize(nextDate));
  };

  const handleNext = () => {
    if (disabled) return;
    const currentDate = parseISO(effectiveDate);
    const nextDate = mode === 'month' ? addMonths(currentDate, 1) : addDays(currentDate, 1);
    setDate(normalize(nextDate));
  };

  // When the text field is clicked, open the native date picker
  const handleTextFieldClick = () => {
    if (disabled) return;
    if (dateInputRef.current) {
      dateInputRef.current.showPicker ? dateInputRef.current.showPicker() : dateInputRef.current.click();
    }
  };

  // When the user types in the text field, parse and update the date
  const handleTextFieldChange = (e) => {
    if (disabled) return;
    const value = e.target.value;
    // Accept YYYY/MM/DD or YYYY-MM-DD
    const normalized = value.replace(/\//g, "-");
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      const parsed = parseISO(normalized);
      setDate(normalize(parsed));
    }
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
      <AsyncButton
        variant="contained"
        onClick={handlePrev}
        disabled={disabled}
      >
        {mode === 'month' ? t("dateSelector.previousMonth") : t("dateSelector.previousDay")}
      </AsyncButton>
      <Box display="flex" alignItems="center">
        <TextField
          label={mode === 'month' ? t("dateSelector.selectMonth") : t("dateSelector.selectDate")}
          value={format(parseISO(effectiveDate), "yyyy/MM/dd")}
          onChange={handleTextFieldChange}
          onClick={handleTextFieldClick}
          variant="standard"
          disabled={disabled}
          sx={{ maxWidth: 95 }}
        />
        <input
          ref={dateInputRef}
          type="date"
          value={effectiveDate}
          onChange={e => {
            if (disabled || typeof setDate !== 'function') return;
            const parsed = parseISO(e.target.value);
            setDate(normalize(parsed));
          }}
          disabled={disabled}
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
            height: 0,
          }}
        />
        <IconButton
          onClick={handleTextFieldClick}
          size="small"
          sx={{ ml: 1 }}
          disabled={disabled}
        >
          <CalendarTodayIcon fontSize="small" />
        </IconButton>
      </Box>
      <AsyncButton
        variant="contained"
        onClick={handleNext}
        disabled={disabled}
      >
        {mode === 'month' ? t("dateSelector.nextMonth") : t("dateSelector.nextDay")}
      </AsyncButton>
    </Box>
  );
};

export default DateSelector;
